import {HardwareItem} from "../../../../../../api/Hardware";
import {noPrep} from "../../../extras/PrepMenuOptions";

// Whether a hardware item carries a door and/or frame prep. Drives which
// crosshair(s) get placed for an item and the "Door / Frame / Both" indicator in
// the hardware picker.
//
// KEEP IN SYNC with the Go PrepForDoor / PrepForFrame in
// core/hardware/doorhwitems/main.go. There, every branch decides a prep exists
// with `cfg.Name != "" || (cfg.File.Valid && cfg.File.Int > 0)` — i.e. the prep
// name is non-empty OR its custom-template file id is > 0. We only need presence
// here (not the opening-type / handing / hinge-qty short-circuits the Go applies),
// so this is the distilled form of that same rule.
function present(name: string | undefined, customTemplate: number | undefined): boolean {
    if(name === noPrep) return false;
    return (!!name && name.length > 0) || (!!customTemplate && customTemplate > 0);
}

// door prep = any of the single / pair-active / pair-both-active / pair-inactive
// door prep fields is set (see PrepForDoor).
export function hasDoorPrep(item: HardwareItem): boolean {
    return present(item.hwPrepSingleActiveDoor, item.hwPrepSingleActiveDoorCustomTemplate)
        || present(item.hwPrepPairActiveDoor, item.hwPrepPairActiveDoorCustomTemplate)
        || present(item.hwPrepPairBothActiveDoor, item.hwPrepPairBothActiveDoorCustomTemplate)
        || present(item.hwPrepPairInactiveDoor, item.hwPrepPairInactiveDoorCustomTemplate);
}

// Whether the item has a placed quantity on the given leaf, honouring the hinge
// override qty when set. `active` selects the active vs inactive leaf.
export function hasQty(item: HardwareItem, active: boolean): boolean {
    if(active) {
        return item.overrideHingeActiveQty ? item.overrideHingeActiveQty > 0 : item.activeQty > 0;
    }
    return item.overrideHingeInActiveQty ? item.overrideHingeInActiveQty > 0 : item.inactiveQty > 0;
}

// Whether a door placement belongs to the given leaf — matched on the placement's own `side`. An item
// on both leaves of a pair has a separate placement per leaf (see MdHardware.add / ensurePrimaries), so
// each leaf draws only its own; their positions (and drag) stay independent. Structural param to avoid
// a cycle with MdHardware.
export function placedOnLeaf(placement: {target: "door" | "frame"; side: "active" | "inactive"}, active: boolean): boolean {
    return placement.target === "door" && placement.side === (active ? "active" : "inactive");
}

// Whether a placement is drawn on the door elevation for the given leaf: only the
// primary handle + deadbolt preps, which the view shows as bored holes. Every
// other placed item is a hardware-elevation concern. Structural param to avoid a
// cycle with MdHardware.
export function drawnOnDoorElev(placement: {target: "door" | "frame"; side: "active" | "inactive"; kind: "handle" | "lock" | "generic"}, active: boolean): boolean {
    return placedOnLeaf(placement, active) && placement.kind !== "generic";
}

// frame prep = the single-frame or pair-frame prep field is set (see PrepForFrame).
export function hasFramePrep(item: HardwareItem): boolean {
    return present(item.hwPrepSingleFrame, item.hwPrepSingleFrameCustomTemplate)
        || present(item.hwPrepPairFrame, item.hwPrepPairFrameCustomTemplate);
}

// Hinges are placed + edited separately (MdHinges / the hinge tools), so they're
// excluded from the placeable-hardware list. A hinge is any item whose category /
// category2 / category3 is exactly "Hinges" (case-insensitive).
export function isHinge(item: HardwareItem): boolean {
    return [item.category, item.category2, item.category3]
        .some(c => !!c && c.toLowerCase() === "hinges");
}
